import React, { useState } from 'react';
import { Instagram, Facebook } from 'lucide-react';
import { mockData } from '../utils/mock';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import { useLanguage } from '../context/LanguageContext';

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const Footer = ({ onOpenGallery, onOpenAffiliate, onOpenCollaborators }) => {
  const { language, toggleLanguage, t } = useLanguage();
  const { location, contact } = mockData;
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const scrollToSection = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  const openLink = (url) => window.open(url, '_blank', 'noopener,noreferrer');

  const colTitle = {
    fontSize: '11px',
    letterSpacing: '3px',
    textTransform: 'uppercase',
    color: '#C9A84C',
    fontWeight: 600
  };
  const linkStyle = { fontSize: '13px', fontWeight: 300, color: '#C2C1B6' };
  const linkClass = 'font-body block text-left hover:text-[#F5F5F0] transition-colors duration-200';

  const socialBtn = {
    width: '40px',
    height: '40px',
    border: '1px solid rgba(201,168,76,0.18)',
    color: '#C2C1B6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.2s ease, border-color 0.2s ease'
  };

  const navItems = [
    { label: t('footer.navItems.inicio'), action: () => scrollToSection('hero') },
    { label: t('footer.navItems.nosotros'), action: () => scrollToSection('about') },
    { label: t('footer.navItems.menu'), action: () => scrollToSection('menu') },
    { label: t('nav.ubicacion'), action: () => scrollToSection('location') }
  ];

  const programItems = [
    { label: t('nav.afiliados'), action: onOpenAffiliate },
    { label: t('nav.colaboradores'), action: onOpenCollaborators },
    { label: t('nav.galeria'), action: onOpenGallery }
  ];

  return (
    <footer style={{ background: '#141414', borderTop: '1px solid rgba(201,168,76,0.18)' }} data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-[60px] py-16">
        <div className="grid grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_1fr] gap-10 lg:gap-12">
          {/* Col 1 - Brand */}
          <div className="col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center" style={{ width: '38px', height: '38px', background: '#C8282B', borderRadius: '8px' }}>
                <span className="text-white font-bold" style={{ fontSize: '20px' }}>山</span>
              </span>
              <span className="font-display text-white" style={{ fontSize: '24px', fontWeight: 700 }}>Yamauchi</span>
            </div>
            <p className="font-body mt-5" style={{ fontSize: '13px', fontWeight: 300, color: '#C2C1B6', lineHeight: 1.8, maxWidth: '300px' }}>
              {t('footer.tagline')}
            </p>
            <div className="flex gap-3 mt-6">
              {[
                { key: 'instagram', icon: <Instagram className="w-4 h-4" />, url: location.links.instagram },
                { key: 'facebook', icon: <Facebook className="w-4 h-4" />, url: location.links.facebook },
                { key: 'tiktok', icon: <TikTokIcon />, url: location.links.tiktok }
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => openLink(s.url)}
                  style={socialBtn}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#C9A84C'; e.currentTarget.style.borderColor = '#C9A84C'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#C2C1B6'; e.currentTarget.style.borderColor = 'rgba(201,168,76,0.18)'; }}
                  aria-label={s.key}
                  data-testid={`footer-social-${s.key}`}
                >
                  {s.icon}
                </button>
              ))}
            </div>
          </div>

          {/* Col 2 - Navigation */}
          <div>
            <h4 className="font-body" style={colTitle}>{t('footer.navigation')}</h4>
            <div className="mt-5 space-y-3">
              {navItems.map((item) => (
                <button key={item.label} onClick={item.action} className={linkClass} style={linkStyle}>
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Col 3 - Programs */}
          <div>
            <h4 className="font-body" style={colTitle}>{t('footer.programs')}</h4>
            <div className="mt-5 space-y-3">
              {programItems.map((item) => (
                <button key={item.label} onClick={item.action} className={linkClass} style={linkStyle}>
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Col 4 - Contact */}
          <div>
            <h4 className="font-body" style={colTitle}>{t('contactLabel')}</h4>
            <div className="mt-5 space-y-3">
              <button
                onClick={() => openLink(`https://mail.google.com/mail/?view=cm&to=${contact.email}&su=Consulta%20desde%20la%20web`)}
                className={linkClass}
                style={linkStyle}
              >
                {contact.email}
              </button>
              <button
                onClick={() => openLink(location.links.googleMaps)}
                className={linkClass}
                style={linkStyle}
              >
                {location.address}
              </button>
              <span className="font-body block" style={linkStyle}>
                {t('location.days.thursday')} — {t('location.days.saturday')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div style={{ borderTop: '1px solid rgba(201,168,76,0.18)' }}>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-[60px] py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <p className="font-body" style={{ fontSize: '12px', fontWeight: 300, color: '#C2C1B6' }}>
              © 2024 Yamauchi Ramen. {t('footer.rights')}.
            </p>
            <button onClick={() => setShowPrivacy(true)} className="font-body hover:text-[#F5F5F0] transition-colors duration-200" style={{ fontSize: '12px', fontWeight: 300, color: '#C2C1B6' }}>
              {t('footer.privacy')}
            </button>
            <button onClick={() => setShowTerms(true)} className="font-body hover:text-[#F5F5F0] transition-colors duration-200" style={{ fontSize: '12px', fontWeight: 300, color: '#C2C1B6' }}>
              {t('footer.terms')}
            </button>
          </div>
          <button
            onClick={toggleLanguage}
            className="font-body text-[12px] px-3 py-1.5 border text-[#C9A84C] hover:bg-[#C9A84C] hover:text-[#0A0A0A] transition-colors duration-200"
            style={{ borderColor: 'rgba(201,168,76,0.45)', letterSpacing: '1px' }}
            data-testid="footer-language-toggle"
          >
            {language === 'es' ? 'ES / EN' : 'EN / ES'}
          </button>
        </div>
      </div>

      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
      {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
    </footer>
  );
};

export default Footer;
